<script lang="ts" setup>
import { Popup, showLoadingToast } from "vant";
import { useI18n } from "vue-i18n";
import { computed, ref, watch } from "vue";
import { useNodeTypeText } from "@/use/common";
import DialogPreviewItem from "@/components/mobile/gal-game/editor/components/DialogPreviewItem.vue";
import { useStoryGameEditor } from "@/stores/storyGameEditor";

// Props & Emit
const props = defineProps<{
  show: boolean;
  initialNodeId: number;
}>();
const emit = defineEmits<{
  (e: "update:show", show: boolean): void;
  (e: "confirm", dialogIds: number[]): void;
}>();

// State
const { t } = useI18n();
const galGameEditorStore = useStoryGameEditor();
const showSelectedPopup = ref(false);
const nodeId = ref(props.initialNodeId);
const nodeList = computed(() =>
  galGameEditorStore.nodes.map((n) => galGameEditorStore.nodeMap[n])
);
const dialogList = computed(() =>
  galGameEditorStore.dialogList.map((d) => galGameEditorStore.dialogMap[d])
);
const selectedDialogIds = ref<Set<number>>(new Set());
const selectedNodeId2Dialogs = computed(() => {
  return Array.from(selectedDialogIds.value)
    .map((d) => galGameEditorStore.dialogMap[d])
    .reduce((acc, cur) => {
      if (!acc[cur.nodeId]) {
        acc[cur.nodeId] = [];
      }
      acc[cur.nodeId].push(cur);
      return acc;
    }, {} as Record<number, GalGameDialog[]>);
});
const selectedNodes = computed(() => {
  return Object.keys(selectedNodeId2Dialogs.value)
    .map((nodeId) => galGameEditorStore.nodeMap[Number(nodeId)])
    .sort((a, b) => {
      if (a.type === b.type) {
        return a.order! - b.order!;
      }
      return a.type - b.type;
    });
});

// Lifecycle
watch(nodeId, async (nodeId) => {
  const loading = showLoadingToast("");
  try {
    galGameEditorStore.isInitDialog = false;
    await galGameEditorStore.initDialog(nodeId);
  } finally {
    loading.close();
  }
});

watch(
  () => props.show,
  (show) => {
    if (show) reset();
  }
);

// Event Handler
const reset = () => {
  nodeId.value = props.initialNodeId;
  selectedDialogIds.value = new Set<number>();
  showSelectedPopup.value = false;
};
const goBack = () => {
  emit("update:show", false);
};
const onChangeNode = async (e: Event) => {
  nodeId.value = Number((e.target as HTMLSelectElement).value);
};
const onSelectDialog = (dialogId: number) => {
  if (selectedDialogIds.value.has(dialogId)) {
    selectedDialogIds.value.delete(dialogId);
  } else {
    selectedDialogIds.value.add(dialogId);
  }
};
const getNodeName = (node: GalGameNode) => {
  return `${useNodeTypeText(node.type)}: ${
    node.name ? node.name : t("game.unnamedNode")
  }`;
};
</script>
<template>
  <popup
    :show="show"
    class="h-[60%] grid grid-rows-[auto_1fr] overflow-hidden"
    position="bottom"
    round
    @update:show="emit('update:show', $event)"
  >
    <threads-nav :go-back="goBack" show-back>
      <template #center>{{ $t("Message.chooseConversation") }}</template>
      <template #right>
        <button
          :disabled="selectedDialogIds.size === 0"
          class="btn btn-sm btn-primary"
          @click="emit('confirm', Array.from(selectedDialogIds))"
        >
          {{ t("Buttons.confirm") }}
        </button>
      </template>
    </threads-nav>
    <div class="overflow-y-auto relative w-full h-full">
      <div
        class="flex justify-between items-center gap-1 rounded overflow-hidden px-2 py-1"
      >
        <select class="select select-sm select-bordered" @change="onChangeNode">
          <option
            v-for="(item, idx) in nodeList"
            :key="item.id"
            :disabled="item.id === nodeId"
            :selected="item.id === nodeId"
            :value="item.id"
          >
            {{ idx + 1 }}. {{ getNodeName(item) }}
          </option>
        </select>
        <button
          :disabled="selectedDialogIds.size === 0"
          class="btn btn-sm"
          @click="showSelectedPopup = true"
        >
          {{ `${t("Buttons.selected")} (${selectedDialogIds.size})` }}
        </button>
      </div>
      <div class="grid grid-cols-3 gap-1 w-full p-2">
        <div
          v-for="(item, idx) in dialogList"
          :key="item.id"
          class="relative w-fit"
        >
          <dialog-preview-item
            :class="{
            'border-primary': selectedDialogIds.has(item.id!),
            'border-base-200/20': !selectedDialogIds.has(item.id!),
          }"
            :dialog="item"
            :index="idx"
            @click="onSelectDialog(item.id!)"
          />
          <check-circle-fill-icon
            v-if="selectedDialogIds.has(item.id!)"
            class="absolute right-2 top-2 text-primary icon-base"
          />
        </div>
        <div
          v-if="dialogList.length === 0"
          class="w-32 md:w-48 aspect-[16/9] flex items-center justify-center bg-base-100 rounded"
        >
          {{ t("game.noDialog") }}
        </div>
      </div>
    </div>
  </popup>
  <popup
    :close-on-click-overlay="false"
    :show="showSelectedPopup"
    class="h-[50%] grid grid-rows-[auto_1fr] overflow-hidden"
    position="bottom"
    round
    @update:show="emit('update:show', $event)"
  >
    <threads-nav
      :go-back="
        () => {
          showSelectedPopup = false;
        }
      "
      show-back
    >
      <template #center>{{ t("Buttons.selected") }}</template>
      <template #right>
        <button
          class="btn btn-sm btn-primary"
          @click="showSelectedPopup = false"
        >
          {{ t("Buttons.confirm") }}
        </button>
      </template>
    </threads-nav>
    <div class="overflow-y-auto relative w-full h-full">
      <div v-if="selectedDialogIds.size > 0" class="p-2 flex flex-col gap-2">
        <div
          v-for="node in selectedNodes"
          :key="node.id"
          class="relative w-fit flex flex-col bg-neutral rounded overflow-hidden p-2 gap-2"
        >
          <div class="font-bold">
            {{ getNodeName(node) }}
          </div>
          <div class="grid grid-cols-3 gap-1 w-full">
            <div
              v-for="item in selectedNodeId2Dialogs[node.id!]"
              :key="item.id"
              class="relative w-fit"
            >
              <dialog-preview-item
                :class="{
            'border-primary': selectedDialogIds.has(item.id!),
            'border-base-200/20': !selectedDialogIds.has(item.id!),
          }"
                :dialog="item"
                @click="onSelectDialog(item.id!)"
              />
              <check-circle-fill-icon
                v-if="selectedDialogIds.has(item.id!)"
                class="absolute right-2 top-2 text-primary icon-base"
              />
            </div>
            <div
              v-if="dialogList.length === 0"
              class="w-32 md:w-48 aspect-[16/9] flex items-center justify-center bg-base-100 rounded"
            >
              {{ t("game.noDialog") }}
            </div>
          </div>
        </div>
      </div>
      <div
        v-else
        class="flex items-center justify-center w-full h-full text-base-content/50"
      >
        <div class="text-2xl font-bold">{{ t("game.noSelectedDialog") }}</div>
      </div>
    </div>
  </popup>
</template>
